import api from "../utils/api";

const BASE = "/complaints";
const LOCAL_STORAGE_KEY = 'step4_tickets_mock';

// Seed initial mock tickets if none exist in localStorage
const SEED_TICKETS = [
  {
    id: 'TKT-1001',
    subject: 'Meter M-019 reporting 0 scale factor',
    description: 'The scale factor for meter M-019 was observed to be 0 since yesterday. Power readings are showing up as 0 as well. Please review configuration.',
    client_name: 'Al-Hamd Textiles',
    meter_id: 'M-019',
    machine_name: 'Main Generator',
    category: 'Inaccurate Readings',
    priority: 'High',
    status: 'In Progress',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    created_by: 'manager_ahmed',
  },
  {
    id: 'TKT-1002',
    subject: 'Missing data gap on solar inverter meter',
    description: 'We detected a 4-hour missing data gap on 25th July from 12:00 to 16:00. This is affecting the daily production charts.',
    client_name: 'Khaadi Packaging',
    meter_id: 'SOL-004',
    machine_name: 'Solar Inverter 1',
    category: 'Missing Data',
    priority: 'Medium',
    status: 'Open',
    created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    created_by: 'khaadi_admin',
  },
];

function getLocalTickets() {
  const data = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!data) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(SEED_TICKETS));
    return SEED_TICKETS;
  }
  try {
    return JSON.parse(data);
  } catch (err) {
    return SEED_TICKETS;
  }
}

function saveLocalTickets(tickets) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tickets));
}

/**
 * Parses metadata embedded in the description string
 */
function mapBackendComplaintToTicket(c, username) {
  let category = 'Other';
  let meter_id;
  let machine_name;

  const desc = c.description || '';
  const metadataIndex = desc.indexOf('--- Metadata ---');
  if (metadataIndex !== -1) {
    const metaLines = desc.slice(metadataIndex).split('\n');
    for (const line of metaLines) {
      if (line.startsWith('Category:')) {
        category = line.split('Category:')[1].trim();
      } else if (line.startsWith('Meter ID:')) {
        meter_id = line.split('Meter ID:')[1].trim();
      } else if (line.startsWith('Machine Name:')) {
        machine_name = line.split('Machine Name:')[1].trim();
      }
    }
  }

  return {
    id: c.ticket_id,
    subject: c.title,
    description: c.description,
    client_name: username || c.username,
    category,
    priority: c.priority,
    status: c.status,
    created_at: c.created_at,
    created_by: username || c.username,
    meter_id,
    machine_name,
  };
}

/**
 * Fetch all tickets for the client
 */
export async function getTickets(username) {
  try {
    const response = await api.get(`${BASE}/${username}`);
    if (response.data && Array.isArray(response.data)) {
      return response.data.map(c => mapBackendComplaintToTicket(c, username));
    }
  } catch (err) {
    console.warn('Backend API /complaints/{username} unavailable, falling back to localStorage:', err);
  }

  // Fallback to localStorage
  const tickets = getLocalTickets();
  if (username) {
    return tickets.filter(t => t.created_by.toLowerCase() === username.toLowerCase());
  }
  return tickets;
}

/**
 * Create a new complaint ticket
 */
export async function createTicket(req, username) {
  let desc = req.description;
  if (req.category || req.meter_id || req.machine_name) {
    desc += "\n\n--- Metadata ---";
    if (req.category) desc += `\nCategory: ${req.category}`;
    if (req.meter_id) desc += `\nMeter ID: ${req.meter_id}`;
    if (req.machine_name) desc += `\nMachine Name: ${req.machine_name}`;
  }

  const payload = {
    username,
    title: req.subject,
    description: desc,
    priority: req.priority || "High",
  };

  try {
    const response = await api.post(BASE, payload);
    if (response.data && response.data.ticket_id) {
      return {
        id: response.data.ticket_id,
        subject: req.subject,
        description: desc,
        client_name: username,
        category: req.category || 'Other',
        priority: req.priority || 'High',
        status: 'Open',
        created_at: new Date().toISOString(),
        created_by: username,
        meter_id: req.meter_id,
        machine_name: req.machine_name,
      };
    }
  } catch (err) {
    console.warn('Backend API POST /complaints unavailable, saving to localStorage:', err);
  }

  // Local storage save fallback
  const tickets = getLocalTickets();
  const nextNum = tickets.reduce((max, t) => {
    const num = parseInt(t.id.split('-')[1]);
    return isNaN(num) ? max : Math.max(max, num);
  }, 1000) + 1;

  const newTicket = {
    id: `TKT-${nextNum}`,
    ...req,
    description: desc,
    status: 'Open',
    created_by: username,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  tickets.unshift(newTicket);
  saveLocalTickets(tickets);
  return newTicket;
}
